import React, { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import sdgs from '../data/sdgs.json'; // Import SDGs data
import SDGItem from './SdgItem';
import Step from './Step';
import { IRating } from '../types/Rating';
import { SDG, SDGTransformed } from 'sdgselection/types/SDG';
import Rating from './Rating';
import '../styles/sdg-selection.css'; // Import the CSS file
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faCheck } from '@fortawesome/free-solid-svg-icons';
import useAuthUserId from 'auth/hooks/useAuthUser';
import userSdgApi from '../utils/API';
import { LoaderSm } from 'common/components/Loader/Loader';
import { confirmError, confirmSuccess } from 'common/components/confirmation/confirm';
import { pageNames } from 'config/pageNames';

const sdgList: SDGTransformed[] = sdgs as unknown as SDGTransformed[];
const MAX_FINAL = 5;

const SDGSelection = () => {
  const [step, setStep] = useState(1);
  const [selectedSDGs, setSelectedSDGs] = useState<number[]>([]);
  const [finalSDGs, setFinalSDGs] = useState<number[]>([]);
  const [importanceRatings, setImportanceRatings] = useState<IRating>({});
  const [controlRatings, setControlRatings] = useState<IRating>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [description, setDescription] = useState<string | undefined>(undefined);
  const userId = useAuthUserId();
  const navigate = useNavigate();

  const handleSDGSelect = (id: number) => {
    setFinalSDGs((prev: number[]) =>
      prev.includes(id) ? prev.filter((sdg) => sdg !== id) : [...prev, id]
    );
  };

  const handleRateImportance = (id: number, rating: number) => {
    setImportanceRatings((prev) => ({ ...prev, [id]: rating }));
  };

  const handleRateControl = (id: number, rating: number) => {
    setControlRatings((prev) => ({ ...prev, [id]: rating }));
  };

  const getTopSDGs = (): number[] => {
    const combinedRatings = Object.keys(importanceRatings).map((id: string) => ({
      id: Number(id),
      score: importanceRatings[Number(id)] + (controlRatings[Number(id)] || 0),
    }));
    return combinedRatings
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_FINAL)
      .map((item) => item.id);
  };

  const cleanUpFinalSdgs = (topSdgs: number[]) => {
    setFinalSDGs((prev: number[]) => [...prev.filter((id) => topSdgs.includes(id))]);
  };

  const nextStep = () => {
    if (step === 2) {
      const topSdgs = getTopSDGs();
      setSelectedSDGs(topSdgs);
      cleanUpFinalSdgs(topSdgs);
    }
    setStep((prev) => prev + 1);
  };

  const prevStep = () => {
    setStep((prev) => prev - 1);
  };

  const saveSdgs = async (sdgs: number[]) => {
    try {
      await userSdgApi.CreateUserSdg({ title, description, userId, sdgs });
      confirmSuccess({ actionTitle: 'Creating user sdgs' });
      setIsLoading(false);
      navigate(pageNames.SDG_SELECTED);
    } catch (e) {
      confirmError({ actionTitle: 'Create user sdgs' });
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;

    // Trigger native browser validation UI for required fields
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    // Extra guard: ensure exactly 3 goals are selected
    if (finalSDGs.length !== 3) {
      return;
    }

    setIsLoading(true);
    void saveSdgs(finalSDGs);
  };

  const isStep3 = step === 3;

  return (
    <div className="sdg-selection">
      {step === 1 && (
        <Step
          title="Step 1: Assess Importance"
          summary="Rate the importance of each SDG below to your organization or event"
          description="Using the framework of the United Nations Sustainable Development Goals let’s rank how IMPORTANT each goal is to your organisation or your event. Remember the SDGs are bold and ambitious. If you don’t know how to rank the importance of a goal think about what your stakeholders (eg your attendees or your organisation leaders) might think. Don’t over complicate it, you know your event"
        >
          <div className="rating-list">
            {sdgList.map((sdg: SDGTransformed, index) => (
              <Rating
                index={index}
                key={sdg.id}
                sdg={sdg}
                rating={importanceRatings[sdg.id] || 0}
                onRate={handleRateImportance} 
                mode={'importance'} />
            ))}
          </div>
        </Step>
      )}

      {step === 2 && (
        <Step
          title="Step 2: Assess Control"
          summary="Rate the level of control your organization or event has over each SDG below"
          description="Using the framework of the United Nations Sustainable Development Goals let’s rank how much CONTROL you, your event or your organisation have over each goal. Remember the SDGs are bold and ambitious. If you don’t know how to rank the control you have over  a goal think about what your stakeholders (eg your attendees or your organisation leaders) might think. Don’t over complicate it, you know your event."
        >
          <div className="rating-list">
            {sdgList
              .filter((sdg) => importanceRatings[sdg.id])
              .map((sdg: SDGTransformed, index: number) => (
                <Rating
                  index={index}
                  key={sdg.id}
                  sdg={sdg}
                  rating={controlRatings[sdg.id] || 0}
                  onRate={handleRateControl} 
                  mode={'control'}                
                />
              ))}
          </div>
        </Step>
      )}

      {/* STEP 3 AS A FORM */}
      {isStep3 && (
        <form onSubmit={handleSubmit} noValidate={false}>
          <Step
            title="Step 3: Select Top 3 SDGs"
            description="We have ordered the SDG list important to you and in your control based on your ratings limited to 5 SDGs below. Complete the exercise by selecting the top 3 SDGs that you will work with."
          >
            {/* SDG choices */}
            {sdgList
              .filter((sdg) => selectedSDGs.includes(sdg.id))
              .map((sdg: SDGTransformed, index: number) => (
                <SDGItem
                  index={index}
                  key={sdg.id}
                  sdg={sdg}
                  onSelect={handleSDGSelect}
                  isSelected={finalSDGs.includes(sdg.id)}
                />
              ))}

            {/* Title + Description inside the form */}
            <div className="mt-4">
              {/* Title input */}
              <div className="mb-3">
                <label htmlFor="sdgTitle" className="form-label fw-bold">
                  Title <span className="text-danger">*</span>
                </label>
                <input
                  id="sdgTitle"
                  name="sdgTitle"
                  type="text"
                  className="form-control"
                  placeholder="Enter your Goals title/Summary"
                  value={title ?? ''}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={200}
                  required
                />
              </div>

              {/* Description input */}
              <div className="mb-3">
                <label htmlFor="sdgDescription" className="form-label fw-bold">
                  Description <span className="text-danger">*</span>
                </label>
                <textarea
                  id="sdgDescription"
                  name="sdgDescription"
                  className="form-control"
                  placeholder="Enter a brief description of your goals, what you would use them for etc"
                  rows={3}
                  value={description ?? ''}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                ></textarea>
              </div>
            </div>
          </Step>

          {/* Step 3 navigation + submit */}
          <div className="d-flex justify-content-center mt-3 gap-2">
            {step > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="btn btn-outline-secondary py-3 px-4"
              >
                <FontAwesomeIcon icon={faArrowLeft} /> Back
              </button>
            )}

            {/* Final submit button uses form submit now */}
            <button
              type="submit"
              className="btn btn-outline-success py-3 px-4"
              disabled={finalSDGs.length !== 3 || isLoading}
            >
              {finalSDGs.length !== 3
                ? 'Select 3 Goals To Proceed'
                : 'Confirm Selection'}{' '}
              {finalSDGs.length === 3 && !isLoading && (
                <FontAwesomeIcon icon={faCheck} />
              )}
              {isLoading && <LoaderSm />}
            </button>
          </div>
        </form>
      )}

      {/* Navigation buttons for steps 1 & 2 */}
      {!isStep3 && (
        <div className="d-flex justify-content-center mt-3 gap-2">
          {step > 1 && (
            <button
              type="button"
              onClick={prevStep}
              className="btn btn-outline-secondary py-3 px-4"
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Back
            </button>
          )}

          {step < 3 && (
            <button
              type="button"
              onClick={nextStep}
              className="btn btn-outline-primary py-3 px-4"
            >
              Next <FontAwesomeIcon icon={faArrowRight} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SDGSelection;
